/**
 * Main initialization script for the Historical Figures Image Library
 * Loads and configures the image library when the page loads
 */
import { HistoricalFigureImageLibrary } from "./modules/HistoricalFigureImageLibrary.js";
import { StoryShowcaseController } from "./modules/StoryShowcaseController.js";
import { getStoryData } from "./modules/StoryData.js";

// Initialize components when the DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
    console.log("Initializing historical figures image library...");

    // Create and initialize the image library
    const imageLibrary = new HistoricalFigureImageLibrary();
    imageLibrary.init();

    // Create and initialize the story showcase controller
    const storyShowcase = new StoryShowcaseController();

    // Store the instances in the window object for debugging
    window.historicalFiguresImageLibrary = imageLibrary;
    window.storyShowcaseController = storyShowcase;

    console.log("Historical figures image library initialized");
    console.log("Story showcase controller initialized");

    // Add event listener for the bio button
    const bioButton = document.getElementById("bio-button");
    if (bioButton) {
        console.log("Bio button found, adding event listener");
        bioButton.addEventListener("click", () => {
            console.log("Bio button clicked");

            // Allow a moment for the bio panel to be created and become visible
            setTimeout(() => {
                const characterName = imageLibrary.getCurrentCharacterFromDOM();
                if (characterName) {
                    console.log(
                        `Loading images for ${characterName} after bio button click`,
                    );
                    imageLibrary.loadCharacterImages(characterName);
                } else {
                    console.log(
                        "Could not determine current character after bio button click",
                    );
                }
            }, 300);
        });
    } else {
        console.warn("Bio button not found");
    }

    // Add event listener for the story button
    const storyButton = document.getElementById("story-button");
    if (storyButton) {
        console.log("Story button found, adding event listener");
        storyButton.addEventListener("click", () => {
            console.log("Story button clicked");

            // Close bio panel if it's open
            if (window.bioController && window.bioController.isPanelActive) {
                console.log(
                    "Bio panel is open, closing it before opening story showcase",
                );

                // Reset bio camera animation before closing the panel
                console.log(
                    "BioController: Resetting camera due to story opening while bio active",
                );
                window.bioController.resetBioCameraForInterfaceOpening("story");

                window.bioController.toggleBioPanel(false);
            }

            // Get current character ID from localStorage or model viewer
            const currentCharacterId = getCurrentCharacterIdForStory();

            if (currentCharacterId) {
                console.log(
                    `Opening story showcase for character: ${currentCharacterId}`,
                );
                storyShowcase.openStoryShowcase(currentCharacterId);
            } else {
                console.warn(
                    "No character selected or character not found for story showcase",
                );
            }
        });
    } else {
        console.warn("Story button not found");
    }

    // Listen for bio panel events
    document.addEventListener("bioPanelOpened", (event) => {
        if (event.detail && event.detail.name) {
            console.log(
                `Bio panel opened event received for ${event.detail.name}`,
            );
            imageLibrary.loadCharacterImages(event.detail.name);
        }
    });
});

/**
 * Get the current character ID for story showcase
 * Maps model IDs to story character IDs
 */
function getCurrentCharacterIdForStory() {
    // Try to get from localStorage first
    const storedModelId = localStorage.getItem("selectedModelId");

    // Try to get from model viewer if available
    const modelViewer = window.modelViewer;
    const currentModelKey = modelViewer?.currentModelKey;

    // Use stored model ID or current model key
    const modelId = storedModelId || currentModelKey;

    if (!modelId) {
        return null;
    }

    // Map model IDs to story character IDs
    const modelToStoryMap = {
        "bc5d31a8-047b-4f72-8f16-fea7a365ca69": "richard-feynman", // Richard Feynman
        "88c314c4-5890-4e55-9418-8b46a376ef81a": "nietzsche", // Friedrich Nietzsche
        "01db85dc-81e0-4433-9440-a54ce4976178": "erwin-schrodinger", // erwin-schrodinger
        "2db1d398-0161-46cd-8aff-53008d29a9dc": "shannon", // claude shannon
        "c2cc71ce-89e7-4c94-9441-eec86ec67a60": "napoleon-bonaparte", //napoleon-bonaparte
        "0c6099ed-c0db-4dd7-bc46-1d720324db5a": "michael-faraday", // Michael Faraday
        "1a35136c-42d8-46c7-a189-c4b32da87832": "john-neumann", // John Neumann
        "be0dcf0f-c72b-48f7-a462-6ba7560498d7": "alan-turing", // Alan Turing
        "d537d61d-0313-421b-ae66-48a8d7222b3f": "nikola-tesla", // Nikola Tesla
        "16f5b6a1-45f6-4c01-a612-60d4e4ab2297": "rene-descartes", // René Descartes
        "5eb1ad8d-4c11-4174-b79a-c232991d015b": "oppenheimer", // Oppenheimer
        "7021ffd6-51bb-41a8-920e-935899dcd631": "isaac-newton", // Isaac Newton
        "93ea7051-9722-443d-90fb-18a84c51894c": "james-clerk-maxwell", // James Clerk Maxwell
        "b20dc26f-8924-4aee-9ef5-9b98c0367a34": "ernest-hemingway", // Ernest Hemingway
        "73f610bf-d9c9-4d9f-a026-a10086a66725": "marie-curie", // Marie Curie
        "802bd089-d231-4c4e-830c-476199321af8": "niels-bohr", // Niels Bohr
        "992e9007-b4f8-4711-80c2-884daf3d9960": "van-gogh", // Van Gogh
        "770e2d93-a6a4-41ed-aca1-8951327885b5": "amelia-earhart", // Amelia Earhart
        "5a16ff56-6e03-414e-a89d-cda9d83fae60": "henry-ford", // Henry Ford
        "676f8b15-4a88-46b7-8842-8ade63743fb6": "test", // Test
        "be0edc3a-e50d-4579-86b8-529db601fdd1": "rechard-feynman-cc", // Rechard Feynman cc
        "031f2eea-64e5-441d-a37d-03d260cc43dc": "abraham-lincoln", // Abraham Lincoln
        "a59fe2c1-9046-405a-9a99-a7ddbaee13be": "adam-smith", // Adam Smith
        "e3f55c33-3ff0-471f-b833-b59d7b145fc6": "al-capone", // Al Capone
        "c4b9ac58-a66a-451e-b035-afabd8be50b9": "aristotle", // Aristotle
        "b8951237-698b-42b5-8b6e-ee381b91fb65": "confucius", // Confucius
        "9ef81498-3fb9-4175-a6d4-c2a02d71c640": "cyrus-the-great", // Cyrus The Great
        "897eb00c-4f16-4f73-81d5-d1c91fad53f2": "charles-darwin", // Charles Darwin
        "8944783c-2e2b-4796-b92e-21dacca776bc": "leonardo-da-vinci", // Leonardo da Vinci
        "d6e3a3f1-d31e-4aab-8dd0-8b820932bd13": "enrico-fermi", // Enrico Fermi
        "8f109c00-f7d2-4b9b-b44e-740a55e4339e": "george-washington", // George Washington
        "4d7d2e17-b818-4144-b78b-629c688a71ac": "karl-marx", // Karl Marx
        "2baa0479-7e38-4473-a2f2-1ad54149dbac": "muhammad-ali", // Muhammad Ali
        "9039a447-b6eb-4de2-9927-0b0c6476c090": "malcolm-x", // Malcolm X
        "3f14429d-b2d4-4b99-8a05-58eed567c0d3": "mansa-musa", // Mansa Musa
        "e97568c5-1daa-4a95-b4ae-0fe374c6b083": "marcus", // Marcus Aurelius
        "d0c97034-328e-4538-8107-a9825d0c1594": "martin-luther-king", // Martin Luther King
        "7716e2f6-eb48-424d-a962-252b15ca634e": "newton", // Newton
        "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b": "niccolo-machiavelli", // Niccolo Machiavelli
        "8ded3849-e5ad-4dea-9bf5-3d8f912e29cf": "robert-oppenheimer", // Robert Oppenheimer
        "59c67a42-8c1e-45b8-aa41-b6fe6b3f5629": "george-orwell", // George Orwell
        "3542f19e-4e07-4828-8f9d-f330e4d6d660": "plato", // Plato
        "d75e63e0-2b2b-43ef-933f-589e8d6818c0": "salvador-dali", // Salvador Dali
        "548fe879-e63c-4d6c-a377-525f4f9e12c8": "satoshi-nakamoto", // Satoshi Nakamoto
        "d9f9af52-ae93-4bd2-abd4-0b7032844ddd": "nikola-tesla", // Nikola Tesla
        "7c7ac397-c36e-4218-92d4-f437d1959cca": "euclid", // Euclid
        "f9f3f836-00c6-4160-8416-4b71b53f1307": "ramanujan", // Srinivasa Ramanujan
        "5802e813-e715-491d-a0fd-92dc643c458e": "pascal", // Blaise Pascal
        "191e6103-a42c-4132-926c-230e7b1be5b6": "bernoulli", // Jacob Bernoulli
        "6bcf3678-e232-46c9-996a-67beb3fd2dbf": "khwarizmi", // Al-Khwarizmi
        "14836d13-ace6-4bf8-be26-26fbb80ed9bc": "socrates", // Socrates
        "3841a6bb-f977-453d-92f5-6cec1267546f": "freud", // Sigmund Freud
        "c0063a05-916b-45d0-bc2c-c63210414b17": "ada", // Ada Lovelace
        "7f61d092-99e4-48e6-bf80-7aa2b7bd98ff": "gauss", // Carl Friedrich Gauss
        "127cd993-f16e-4659-84fa-882cba633097": "galileo", // Galileo Galilei
        "94b6efbe-0aaa-47c7-bd3f-6d4f441d767f": "hopper", // Grace Hopper
        "c93478a1-ed2f-4f1a-81f6-3fa474a21566": "lagrange", // Joseph-Louis Lagrange
        "5a39bed2-a551-4a03-98a2-46104a3324bf": "euler", // Leonhard Euler
        "2707ca40-df64-4258-97c8-67d3695e8a42": "brahmagupta", // Brahmagupta
        "e617290b-bda0-4771-91ac-3fe6939dd412": "franklin", // Benjamin Franklin
        "a2a43496-9a83-4444-813e-18a1930296e6": "khayyam", // Omar Khayyam
        "78376372-3d71-4fce-8650-82b5be2c006b": "diophantus", // Diophantus
        "ca6ebdca-5151-4a58-8523-56d25fce5dfa": "playfair", // William Playfair
        "e3428624-bd1e-48b9-ae8c-c66ba68879cc": "boole", // George Boole
        "3140d634-c7ca-4bda-af36-e1b49978b201": "bayes", // Thomas Bayes
        "c73fed54-81e3-46ec-9d7c-3d11d05b142d": "eratosthenes", // Eratosthenes
        "ece0ab37-37c1-4ead-8222-fbb5b185dbe2": "volta", // Alessandro Volta
        "d13b5a81-ed77-4a25-8a01-a76a0165b349": "archimedes", // Archimedes
        "8f67d579-41c2-456d-900b-fccbcbf46bd3": "viete", // François Viète
        "ec6c5b20-8ac6-42bd-b291-f248cb3f0ebc": "klein", // Felix Klein
        "44f87c0b-ca22-4225-9fea-8d823e6a0160": "warren-buffett", // Warren Buffett
        "581e7293-43cf-45ea-9fb3-0955c563cbe2": "genghis-khan", // Genghis Khan
        "4f50a698-6034-49c1-b9d4-221f59bf2378": "kahlo", // Frida Kahlo
        "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9": "nostradamus", // Nostradamus
        "278dddd6-b4a8-4303-a41d-be6219dbc907": "einstein", // Einstein
    };

    const storyCharacterId = modelToStoryMap[modelId];

    console.log(
        `Model ID: ${modelId}, Story Character ID: ${storyCharacterId}`,
    );

    return storyCharacterId;
}

// Lab UI merge: expose the story-data getters so the mockup slide-in Story
// panel (templates/index.html) can render real per-character episodes without
// opening the StoryShowcaseController modal. getStoryDataForCurrent() reuses
// the existing UUID->slug resolution above so there is no map duplication.
if (typeof window !== "undefined") {
    window.getStoryData = getStoryData;
    window.getStoryCharacterSlugForCurrent = getCurrentCharacterIdForStory;
    window.getStoryDataForCurrent = function () {
        try {
            const slug = getCurrentCharacterIdForStory();
            return slug ? getStoryData(slug) : null;
        } catch (e) {
            return null;
        }
    };
}
